import { PRODUCT_CONFIGURATION } from '../product/product-configuration'
import { effect, Injectable, untracked, inject } from '@angular/core'
import { apiEndpoint } from '@awork/environments/environment'
import type { INpsUserSetting, UserSetting, UserSettingValue } from '@awork/_shared/models/user-setting.model'
import type { Observable } from 'rxjs'
import {
  BehaviorSubject,
  catchError,
  concatMap,
  debounce,
  debounceTime,
  EMPTY,
  endWith,
  ignoreElements,
  defer,
  filter,
  finalize,
  groupBy,
  isObservable,
  map,
  mergeMap,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  tap,
  throwError,
  timer,
  withLatestFrom
} from 'rxjs'
import { HttpErrorResponse, type HttpResponse } from '@angular/common/http'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import type {
  IFrameworkViewSetting,
  IProjectTaskListSetting,
  IProjectViewSetting,
  IViewPersistenceSetting,
  ITaskViewListsSetting
} from '@awork/framework/models/view-setting.model'
import { SettingsStore } from '@awork/framework/state/settings.store'
import { AppQuery } from '@awork/core/state/app.query'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { Level } from '@awork/_shared/services/log-service/types'
import { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import { parseJSON } from '@awork/_shared/functions/lodash'
import type { SortByOption } from '@awork/_shared/models/sort-by-option.model'
import { SettingsQuery } from '@awork/framework/state/settings.query'
import type { EntitySettingsKey, EntitySettingsValue, SettingsState } from '@awork/framework/state/settings.types'
import { SortByOptions } from '@awork/framework/state/settings.types'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { deepEqual } from '@awork/_shared/functions/lodash'

export interface ILastUsedEntityTypeSetting {
  workTypeIdTasks?: string
  workTypeIdTimes?: string
}

export type EntityName = 'projects' | 'tasks' | 'users'

interface EntityUserSettingsQueueItem {
  key: keyof SettingsState
  entityName: EntityName
  entityId: string
}

export interface FetchUserSettingsOptions {
  /** Keeps fields changed locally after the request started when the server response is applied. */
  preserveLocalChanges?: boolean
}

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private readonly product = inject(PRODUCT_CONFIGURATION)
  private apiClient = inject(ApiClient)
  private settingsStore = inject(SettingsStore)
  private settingsQuery = inject(SettingsQuery)
  private appQuery = inject(AppQuery)
  private logService = inject(LogService)
  private workspaceQuery = inject(WorkspaceQuery)

  private url: string
  private viewSetting: UserSetting
  lastUsedEntityType: ILastUsedEntityTypeSetting = {}

  lastUsedWorkTypeIdForTimes$ = new ReplaySubject<string>(1)
  lastUsedWorkTypeIdForTasks$ = new ReplaySubject<string>(1)

  private readonly projectTaskListSaves = new Subject<{
    setting: IProjectTaskListSetting
    result: ReplaySubject<UserSetting>
  }>()

  private readonly pendingProjectTaskListSaves = new Map<string, ReplaySubject<UserSetting>>()

  private saveUserSettingsQueue$ = new Subject<{ key: keyof SettingsState; immediate: boolean; version: number }>()
  private userSettingsSaveVersion = 0
  private readonly pendingUserSettingsSaves$ = new BehaviorSubject<ReadonlyMap<keyof SettingsState, number>>(new Map())
  private saveEntityUserSettingsQueue$ = new Subject<EntityUserSettingsQueueItem>()
  private fetchedUserSettings$ = new BehaviorSubject<(keyof SettingsState)[]>([])
  private fetchedEntityUserSettings$ = new BehaviorSubject<
    { entityName: EntityName; entityId: string; key: keyof SettingsState }[]
  >([])

  private readonly ganttDefaultWorkspaceCreatedOn = new Date('2026-06-23T00:00:00.000Z')

  constructor() {
    this.url = `${apiEndpoint}/me/settings`

    effect(() => {
      const isLoggedIn = this.appQuery.query('isLoggedIn')

      if (isLoggedIn()) {
        // Fetch the last used entity types
        untracked(() => this.fetchLastUsedEntityTypes())
      }
    })

    this.projectTaskListSaves
      .pipe(
        concatMap(({ setting, result }) =>
          this.setUserSetting('projectTaskList', JSON.stringify(setting), 'projects', setting.projectId).pipe(
            tap({
              next: value => result.next(value),
              complete: () => {
                if (this.pendingProjectTaskListSaves.get(setting.projectId) === result) {
                  this.pendingProjectTaskListSaves.delete(setting.projectId)
                }

                result.complete()
              }
            }),
            catchError(error => {
              this.logService.logMessage(Level.error, 'Cannot save project task settings', { error })

              if (this.pendingProjectTaskListSaves.get(setting.projectId) === result) {
                this.pendingProjectTaskListSaves.delete(setting.projectId)
              }

              result.error(error)

              return EMPTY
            })
          )
        )
      )
      .subscribe()

    this.initSaveUserSettingsQueue()
    this.initSaveEntityUserSettingsQueue()
  }

  /**
   * Initializes the save user settings queue.
   * Debounces saves by 2 seconds per key unless an immediate save is requested.
   * Saves each key in order, allowing different keys to be saved in parallel.
   * It will only save the settings if it was fetched before to prevent overwriting.
   */
  private initSaveUserSettingsQueue(): void {
    this.saveUserSettingsQueue$
      .pipe(
        groupBy(item => item.key),
        mergeMap(group$ =>
          group$.pipe(
            debounce(item => (item.immediate ? of(0) : timer(2000))),
            withLatestFrom(this.fetchedUserSettings$),
            filter(([{ key, version }, fetchedKeys]) => {
              if (fetchedKeys.includes(key)) {
                return true
              }

              this.clearPendingUserSettingsSave(key, version)

              return false
            }),
            concatMap(([{ key, version }]) => {
              const storedValue = this.settingsQuery.query(key)
              const value = JSON.stringify(storedValue())

              return this.setUserSetting(key, value).pipe(
                catchError(error => {
                  this.logService.logMessage(Level.error, `Cannot save user setting: ${key}`, { error })
                  return EMPTY
                }),
                finalize(() => this.clearPendingUserSettingsSave(key, version))
              )
            })
          )
        )
      )
      .subscribe()
  }

  /**
   * Clears pending state only when no later save was queued for this key.
   * @param {keyof SettingsState} key Settings key whose save ended or was dropped.
   * @param {number} version Version captured when the save was queued.
   */
  private clearPendingUserSettingsSave(key: keyof SettingsState, version: number): void {
    const pending = this.pendingUserSettingsSaves$.getValue()

    if (pending.get(key) !== version) {
      return
    }

    const remaining = new Map(pending)

    remaining.delete(key)
    this.pendingUserSettingsSaves$.next(remaining)
  }

  /**
   * Waits until this key has no queued or active save. Failed and dropped saves also release waiters.
   * @param {keyof SettingsState} key Settings key to wait for.
   * @returns {Observable<void>} An observable that emits once and completes, immediately if no save is pending.
   */
  whenSaved(key: keyof SettingsState): Observable<void> {
    return this.pendingUserSettingsSaves$.pipe(
      filter(pending => !pending.has(key)),
      take(1),
      map(() => void 0)
    )
  }

  /**
   * Initializes the save entity user settings queue.
   * Saves the entity user settings with a debounce time of 2 seconds per unique combination of key, entityName and entityId.
   * Each combination is debounced independently, allowing multiple entity settings to be saved in parallel.
   * It will only save the settings if it was fetched before to prevent overwriting.
   */
  private initSaveEntityUserSettingsQueue(): void {
    this.saveEntityUserSettingsQueue$
      .pipe(
        groupBy(item => `${item.key}:${item.entityName}:${item.entityId}`),
        mergeMap(group$ =>
          group$.pipe(
            debounceTime(2000),
            withLatestFrom(this.fetchedEntityUserSettings$),
            filter(([item, fetchedKeys]) =>
              fetchedKeys.some(
                key => key.key === item.key && key.entityName === item.entityName && key.entityId === item.entityId
              )
            )
          )
        )
      )
      .subscribe(([{ key, entityName, entityId }]) => {
        const storedSettings = this.settingsQuery.query(key)
        const entitySettingsArray = storedSettings() as { entityName: EntityName; entityId: string }[]
        const entitySetting = entitySettingsArray?.find(
          setting => setting.entityId === entityId && setting.entityName === entityName
        )

        if (entitySetting) {
          const value = JSON.stringify(entitySetting)

          this.setUserSetting(key, value, entityName, entityId).subscribe()
        }
      })
  }

  /**
   * Returns the active view settings.
   * @returns The settings used by the current view.
   */
  get userViewSetting(): UserSetting {
    return this.viewSetting
  }

  /**
   * Sets the active view settings.
   * @param viewSetting Settings for the current view.
   */
  set userViewSetting(viewSetting: UserSetting) {
    this.viewSetting = viewSetting
  }

  /**
   * Makes an API call to request the user settings
   * @returns {Observable<UserSetting[]>} The user settings, is null if there is some error (ex.: not found)
   */
  getUserSettings(): Observable<UserSetting[]> {
    return this.apiClient.get<UserSetting[]>(`${this.url}`)
  }

  /**
   * Makes an API call to request the user setting by key
   * @param {string} key
   * @returns {Observable<HttpResponse<UserSetting>>} The user setting, is null if there is some error (ex.: key not found)
   */
  getUserSetting(key: string): Observable<HttpResponse<UserSetting>> {
    if (key === 'desktopApp' && this.product.id === 'aworkAI') {
      key = 'aworkAIDesktopApp'
    }

    return this.apiClient.get<HttpResponse<UserSetting>>(`${this.url}/${key}`, { observe: 'response' })
  }

  /**
   * Makes an API call to request the user setting by key and returns the parsed value
   * @param {string} key
   * @returns {Observable<UserSettingValue>} The user setting, is null if there is some error (ex.: key not found)
   */
  getUserSettingValue(key: string): Observable<UserSettingValue> {
    return this.apiClient.get<UserSetting>(`${this.url}/${key}`).pipe(
      map(response => {
        const value = {}

        try {
          return JSON.parse(response?.value)
        } catch (e) {
          return value
        }
      })
    )
  }

  /**
   * Makes an API call to request the nps user setting by key
   * @returns {Observable<INpsUserSetting>} The Nps setting, is null if there is some error (ex.: key not found)
   */
  getNpsUserSetting(): Observable<INpsUserSetting> {
    return this.apiClient.get<UserSetting>(`${this.url}/npsRating`).pipe(
      map(response => {
        const npsRatingSettingJSON = parseJSON(response?.value) as INpsUserSetting

        if (!npsRatingSettingJSON) {
          return null
        }

        return {
          lastActionType: npsRatingSettingJSON.lastActionType,
          nextReminder: npsRatingSettingJSON.nextReminder ? new Date(npsRatingSettingJSON.nextReminder) : null
        }
      })
    )
  }

  /**
   * Makes an API call to request the user setting by key
   * @returns {Observable<UserSetting>}
   */
  fetchUserViewSetting(): Observable<UserSetting> {
    return this.getUserSetting('userViewPersistence').pipe(
      tap(() => this.updateFetchedUserSettingsSubject('viewSettings')),
      map(response => {
        let settingValue: IViewPersistenceSetting = {}

        if (response.body) {
          try {
            settingValue = JSON.parse(response.body.value) as IViewPersistenceSetting
          } catch (_) {}
        }

        const normalizedSettingValue = this.normalizeViewSettings(settingValue)

        this.settingsStore.updateViewSettings(normalizedSettingValue)

        if (!response.body) {
          return response.body
        }

        return {
          ...response.body,
          value: JSON.stringify(normalizedSettingValue)
        }
      })
    )
  }

  /**
   * Normalizes view settings to keep persisted data consistent.
   * @param {IViewPersistenceSetting} settings
   * @returns {IViewPersistenceSetting}
   */
  private normalizeViewSettings(settings: IViewPersistenceSetting): IViewPersistenceSetting {
    if (!settings?.framework) {
      return settings || {}
    }

    return {
      ...settings,
      framework: this.normalizeFrameworkSettings(settings.framework)
    }
  }

  /**
   * Normalizes framework settings fields.
   * @param {IFrameworkViewSetting} framework
   * @returns {IFrameworkViewSetting}
   */
  private normalizeFrameworkSettings(framework: IFrameworkViewSetting): IFrameworkViewSetting {
    if (!framework?.pinnedProjects?.length) {
      return framework
    }

    return {
      ...framework,
      pinnedProjects: [...new Set(framework.pinnedProjects)]
    }
  }

  /**
   * Makes an API call to get the user setting with specified key and entity id
   * @param {string} entityName
   * @param {string} entityId
   * @param {string} key
   * @returns {Observable<UserSetting>}
   */
  fetchUserSettingById(entityName: string, entityId: string, key: string): Observable<UserSetting> {
    return this.getUserSettingById(entityName, entityId, key).pipe(map(response => response.body))
  }

  /**
   * Fetches entity settings after pending project task preference saves finish.
   * @param {string} entityName
   * @param {string} entityId
   * @param {string} key
   * @returns {Observable<HttpResponse<UserSetting>>}
   */
  getUserSettingById(entityName: string, entityId: string, key: string): Observable<HttpResponse<UserSetting>> {
    return defer(() => {
      const pendingSave =
        entityName === 'projects' && key === 'projectTaskList'
          ? this.pendingProjectTaskListSaves.get(entityId)
          : undefined

      if (pendingSave) {
        // Wait for completion, then check for saves queued while this one was pending.
        return pendingSave.pipe(
          ignoreElements(),
          catchError(() => EMPTY),
          endWith(null),
          switchMap(() => this.getUserSettingById(entityName, entityId, key))
        )
      }

      return this.fetchAndApplyEntityUserSetting(entityName, entityId, key)
    })
  }

  /**
   * Fetches entity settings and applies the server value to the matching store entry.
   * @param {string} entityName Entity type that owns the settings.
   * @param {string} entityId Entity whose settings are requested.
   * @param {string} key Settings key to fetch.
   * @returns {Observable<HttpResponse<UserSetting>>} The server response after the store update.
   */
  private fetchAndApplyEntityUserSetting(
    entityName: string,
    entityId: string,
    key: string
  ): Observable<HttpResponse<UserSetting>> {
    const taskViewSettings = key === 'taskViewLists' ? this.settingsQuery.queryTaskViewListSettings() : undefined
    const initialTaskViewSetting = untracked(() => taskViewSettings?.().find(setting => setting.id === entityId))

    return this.apiClient
      .get<HttpResponse<UserSetting>>(`${this.url}/${entityName}/${entityId}/${key}`, {
        observe: 'response'
      })
      .pipe(
        map(response => {
          const userSetting: UserSetting = response && response.body ? response.body : null
          let parsedSetting

          if (response?.status === 200 && userSetting?.value) {
            try {
              parsedSetting = JSON.parse(userSetting.value)
            } catch (_) {}
          }

          switch (key) {
            case 'projectTaskList': {
              const defaultProjectTaskListSetting = this.getDefaultProjectTaskListSetting(entityId)
              const projectTaskListSetting = parsedSetting?.projectId ? parsedSetting : defaultProjectTaskListSetting

              this.settingsStore.updateProjectTaskListViewSettings(projectTaskListSetting)
              break
            }
            case 'taskViewLists': {
              const defaultTaskViewListsSetting: ITaskViewListsSetting = {
                id: entityId,
                groupOption: GroupByOptions.none
              }

              const taskViewListsSetting = parsedSetting?.id ? parsedSetting : defaultTaskViewListsSetting

              const currentSetting = untracked(() => taskViewSettings().find(setting => setting.id === entityId))

              // Keep settings copied or edited after this read started.
              if (currentSetting === initialTaskViewSetting) {
                this.settingsStore.updateTaskViewListsSettings(taskViewListsSetting)
              }

              break
            }
            case 'taskListWidget': {
              const defaultTaskListWidgetSetting: ITaskViewListsSetting = {
                id: entityId,
                groupOption: GroupByOptions.upcomingDaily,
                sortingOption: {
                  value: SortByOptions.dueOn,
                  orderBy: 'desc'
                } as SortByOption
              }

              const taskListWidgetSetting = parsedSetting?.id ? parsedSetting : defaultTaskListWidgetSetting

              this.settingsStore.updateTaskListWidgetSettings(taskListWidgetSetting)
              break
            }
            case 'project': {
              const defaultProjectViewSetting: IProjectViewSetting = {
                projectId: entityId
              }

              const projectViewSetting = parsedSetting?.projectId ? parsedSetting : defaultProjectViewSetting

              this.settingsStore.updateProjectViewSettings(projectViewSetting)
              break
            }
            case 'entityTimes':
              if (parsedSetting?.entityId) {
                this.settingsStore.updateEntityTimesSettings(parsedSetting)
              }

              break
          }

          return response
        })
      )
  }

  /**
   * Gets the project task list fallback for projects without a user setting.
   * @param {string} projectId
   * @returns {IProjectTaskListSetting}
   */
  private getDefaultProjectTaskListSetting(projectId: string): IProjectTaskListSetting {
    const workspaceCreatedOn = this.workspaceQuery.getCurrentWorkspace()?.createdOn
    const shouldUseGanttAsDefault = workspaceCreatedOn?.getTime() >= this.ganttDefaultWorkspaceCreatedOn.getTime()

    const defaultProjectTaskListSetting: IProjectTaskListSetting = {
      projectId,
      showDone: false,
      lists: []
    }

    if (shouldUseGanttAsDefault) {
      defaultProjectTaskListSetting.viewMode = 'timeline'
      defaultProjectTaskListSetting.isGanttView = true
    }

    return defaultProjectTaskListSetting
  }

  /**
   * Makes an API call to set or update the user setting with the key and value
   * @param {string} key
   * @param {string} value
   * @param {string} entityName
   * @param {string} entityId
   * @returns {Observable<UserSetting>}
   */
  setUserSetting(key: string, value: string, entityName?: string, entityId?: string): Observable<UserSetting> {
    if (key === 'desktopApp' && this.product.id === 'aworkAI' && !entityName && !entityId) {
      key = 'aworkAIDesktopApp'
    }

    let deprecatedKey: string

    // TODO: Remove this once the view settings usage is refactored
    if (key === 'viewSettings') {
      deprecatedKey = 'userViewPersistence'
    }

    const settingKey = deprecatedKey || key

    return this.apiClient.post<UserSetting>(`${this.url}`, {
      key: settingKey,
      ...(settingKey === 'userViewPersistence' && !entityId && !entityName ? { preservePinnedProjects: true } : {}),
      value,
      entityId,
      entityName
    })
  }

  /**
   * Saves project task preferences in order, even after the caller unsubscribes.
   * @param {IProjectTaskListSetting} setting Complete project preferences to store.
   * @returns {Observable<UserSetting>} The result of this save.
   */
  saveProjectTaskListSettings(setting: IProjectTaskListSetting): Observable<UserSetting> {
    const result = new ReplaySubject<UserSetting>(1)

    this.settingsStore.updateProjectTaskListViewSettings(setting)
    this.pendingProjectTaskListSaves.set(setting.projectId, result)
    this.projectTaskListSaves.next({ setting, result })

    return result.asObservable()
  }

  /**
   * Changes one favourite without sending a stale settings snapshot.
   * @param {string} projectId - The project whose favourite state must change.
   * @param {boolean} isPinned - Whether the project must be a favourite.
   * @returns {Observable<UserSetting>} The saved view settings.
   */
  setPinnedProject(projectId: string, isPinned: boolean): Observable<UserSetting> {
    return this.apiClient.post<UserSetting>(`${this.url}/pinnedprojects`, {
      pinnedProjectId: projectId,
      isPinned
    })
  }

  /**
   * Saves a favourite and merges it into the latest local settings after confirmation.
   * @param {string} projectId Project whose favourite state changes.
   * @param {boolean} isPinned Whether the project should be a favourite.
   * @returns {Observable<UserSetting>} The confirmed save. Keep the subscription until the request completes.
   */
  savePinnedProject(projectId: string, isPinned: boolean): Observable<UserSetting> {
    return this.setPinnedProject(projectId, isPinned).pipe(
      tap(() => {
        const settings = this.settingsQuery.getViewSettings()
        const pinned = settings.framework?.pinnedProjects ?? []
        const pinnedProjects = isPinned ? [...new Set([...pinned, projectId])] : pinned.filter(id => id !== projectId)

        this.settingsStore.updateViewSettings({ ...settings, framework: { ...settings.framework, pinnedProjects } })
      })
    )
  }

  /**
   * Fetches the user settings for the specified key.
   * @param {K} key
   * @param {FetchUserSettingsOptions} options Controls how the server value is applied.
   * @returns {Observable<SettingsState[K]>}
   */
  fetchUserSettings<K extends keyof SettingsState>(
    key: K,
    options?: FetchUserSettingsOptions
  ): Observable<SettingsState[K]> {
    return defer(() => {
      const storedSettings = this.settingsQuery.query(key)
      const initialValue = options?.preserveLocalChanges ? storedSettings() : undefined

      return this.getUserSetting(key).pipe(
        tap(() => this.updateFetchedUserSettingsSubject(key)),
        map(response => {
          if (response.body) {
            try {
              const parsedValue = JSON.parse(response.body.value) as SettingsState[K]
              const resolvedValue = options?.preserveLocalChanges
                ? this.mergeServerSettingsWithLocalChanges(parsedValue, initialValue, storedSettings())
                : parsedValue

              this.settingsStore.updateSettings(key, resolvedValue)

              return resolvedValue
            } catch (error) {
              this.logService.logMessage(Level.error, `Cannot parse user setting: ${error}`, { error })
            }
          }

          return undefined
        })
      )
    })
  }

  /**
   * Applies fields changed locally during a request over the returned server settings.
   * @param {T} serverValue Settings returned by the server.
   * @param {T} initialValue Local settings when the request started.
   * @param {T} currentValue Local settings when the request completed.
   * @returns {T} Reconciled settings.
   */
  private mergeServerSettingsWithLocalChanges<T>(serverValue: T, initialValue: T, currentValue: T): T {
    if (!this.isSettingsObject(serverValue) || !this.isSettingsObject(currentValue)) {
      return serverValue
    }

    const initialSettings = this.isSettingsObject(initialValue) ? initialValue : {}
    const localChanges = Object.fromEntries(
      Object.entries(currentValue).filter(([field, value]) => !deepEqual(value, initialSettings[field]))
    )

    return { ...serverValue, ...localChanges } as T
  }

  /**
   * Checks whether a settings value can be reconciled by field.
   * @param {unknown} value Value to check.
   * @returns {boolean} Whether the value is a non-array object.
   */
  private isSettingsObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  /**
   * Fetches the entity user settings for the specified key
   * @param {EntityName} entityName
   * @param {string} entityId
   * @param {EntitySettingsKey<K>} key
   * @returns {Observable<EntitySettingsValue<K>>}
   * @template K
   */
  fetchEntityUserSettings<K extends keyof SettingsState>(
    entityName: EntityName,
    entityId: string,
    key: EntitySettingsKey<K>
  ): Observable<EntitySettingsValue<K>> {
    return this.apiClient
      .get<{
        entity: EntityName
        key: EntitySettingsKey<K>
        value: string
      }>(`${this.url}/${entityName}/${entityId}/${key}`)
      .pipe(
        tap(() => this.updateFetchedEntityUserSettingsSubject(entityName, entityId, key)),
        map(userSettings => {
          if (userSettings) {
            try {
              const parsedValue = JSON.parse(userSettings.value)

              this.settingsStore.updateEntitySettings(key, entityName, entityId, parsedValue)

              return parsedValue
            } catch (error) {
              this.logService.logMessage(Level.error, `Cannot parse user setting: ${error}`, { error })
            }
          }
        })
      )
  }

  /**
   * Returns whether the user setting for the given key has been fetched from
   * the API during the current session. Used by callers that want to skip a
   * redundant network round-trip when the store value is already authoritative.
   * @param {keyof SettingsState} key
   * @returns {boolean} True when the key has already been fetched.
   */
  hasFetchedUserSetting(key: keyof SettingsState): boolean {
    return this.fetchedUserSettings$.getValue().includes(key)
  }

  /**
   * Updates the fetched user settings keys with the specified key
   * @param {keyof SettingsState} key
   */
  private updateFetchedUserSettingsSubject(key: keyof SettingsState): void {
    const fetchedUserSettings = this.fetchedUserSettings$.getValue()

    if (!fetchedUserSettings.includes(key)) {
      fetchedUserSettings.push(key)
      this.fetchedUserSettings$.next(fetchedUserSettings)
    }
  }

  /**
   * Updates the fetched entity user settings keys with the specified key
   * @param {EntityName} entityName
   * @param {string} entityId
   * @param {keyof SettingsState} key
   */
  private updateFetchedEntityUserSettingsSubject(
    entityName: EntityName,
    entityId: string,
    key: keyof SettingsState
  ): void {
    const fetchedEntityUserSettings = this.fetchedEntityUserSettings$.getValue()

    if (
      !fetchedEntityUserSettings.some(
        item => item.entityName === entityName && item.entityId === entityId && item.key === key
      )
    ) {
      fetchedEntityUserSettings.push({ entityName, entityId, key })
      this.fetchedEntityUserSettings$.next(fetchedEntityUserSettings)
    }
  }

  /**
   * Saves the user settings with the specified key and value.
   * Debounces the API call by 2 seconds unless an immediate save is requested.
   * @param {K} key
   * @param {SettingsState[K]} value
   * @param {{ immediate?: boolean }} options Controls whether to skip the debounce.
   * @param options.immediate
   */
  saveUserSettings<K extends keyof SettingsState>(
    key: K,
    value: SettingsState[K],
    options: { immediate?: boolean } = {}
  ): void {
    const version = ++this.userSettingsSaveVersion

    this.pendingUserSettingsSaves$.next(new Map(this.pendingUserSettingsSaves$.getValue()).set(key, version))
    this.settingsStore.updateSettings(key, value)

    this.saveUserSettingsQueue$.next({ key, immediate: options.immediate === true, version })
  }

  /**
   * Saves the entity user settings with the specified key, entity type and entity id.
   * The API call is debounced by 2 seconds per unique combination of key, entityName and entityId.
   * Each combination is debounced independently, allowing multiple entity settings to be saved in parallel.
   * @param {EntitySettingsKey<K>} key
   * @param {EntityName} entityName
   * @param {string} entityId
   * @param {Partial<EntitySettingsValue<K>>} value
   * @template K
   */
  saveEntityUserSettings<K extends keyof SettingsState>(
    key: EntitySettingsKey<K>,
    entityName: EntityName,
    entityId: string,
    value: Partial<EntitySettingsValue<K>>
  ): void {
    this.settingsStore.updateEntitySettings(key, entityName, entityId, value)

    this.saveEntityUserSettingsQueue$.next({ key, entityName, entityId })
  }

  /**
   * Makes an API call to delete the user setting with specified key and entity id
   * @param {string} entityName
   * @param {string} entityId
   * @param {string} key
   * @returns {Observable<string>}
   */
  deleteUserSettingById(entityName: string, entityId: string, key: string): Observable<string> {
    return this.apiClient.delete<string>(`${this.url}/${entityName}/${entityId}/${key}`)
  }

  /**
   * Makes an API call to delete the user setting by key
   * @param {string} key
   * @returns {Observable<string>}
   */
  deleteUserSetting(key: string): Observable<string> {
    return this.apiClient.delete<string>(`${this.url}/${key}`)
  }

  // ########### LAST USED ENTITY TYPE SETTING ##############
  /**
   * Fetches the last used entity type user setting
   */
  private fetchLastUsedEntityTypes(): void {
    const userSetting$ = this.getUserSetting('lastUsedEntityType')

    if (!isObservable(userSetting$)) {
      return
    }

    userSetting$
      .pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && (error.status === 0 || error.status === 401)) {
            return EMPTY
          }

          return throwError(() => error)
        })
      )
      .subscribe(res => {
        if (res && res.body) {
          try {
            const value: ILastUsedEntityTypeSetting = JSON.parse(res.body.value)

            if (value) {
              this.lastUsedEntityType = value

              // emit events
              this.lastUsedWorkTypeIdForTasks$.next(value.workTypeIdTasks)
              this.lastUsedWorkTypeIdForTimes$.next(value.workTypeIdTimes)
            }
          } catch (error) {
            this.logService.logMessage(Level.error, `Cannot parse entity type setting: ${error}`, { error })
          }
        }
      })
  }

  /**
   * Returns the last used work type id
   * @param {'time' | 'task'} entity
   * @returns {string} The last used work type id
   */
  getLastWorkTypeId(entity: 'time' | 'task'): string {
    if (entity === 'time') {
      return this.lastUsedEntityType.workTypeIdTimes
    } else {
      return this.lastUsedEntityType.workTypeIdTasks
    }
  }

  /**
   * Sets the last used entity type user setting
   * @param {string} workTypeId The last used entity type id
   * @param {'time' | 'task'} entity The entity type whose last used type is to be updated
   */
  setLastUsedWorkTypeSetting(workTypeId: string, entity: 'time' | 'task'): void {
    const currentWorkTypeId =
      entity === 'time' ? this.lastUsedEntityType.workTypeIdTimes : this.lastUsedEntityType.workTypeIdTasks

    if (currentWorkTypeId === workTypeId) {
      return
    }

    // Update the last used entity type
    if (entity === 'time') {
      this.lastUsedEntityType.workTypeIdTimes = workTypeId
      this.lastUsedWorkTypeIdForTimes$.next(workTypeId)
    } else {
      this.lastUsedEntityType.workTypeIdTasks = workTypeId
      this.lastUsedWorkTypeIdForTasks$.next(workTypeId)
    }

    // Save the user setting for the last used entity type
    const stringifiedValue: string = JSON.stringify(this.lastUsedEntityType)

    this.setUserSetting('lastUsedEntityType', stringifiedValue).subscribe({
      error: () => {
        const latestWorkTypeId =
          entity === 'time' ? this.lastUsedEntityType.workTypeIdTimes : this.lastUsedEntityType.workTypeIdTasks

        if (latestWorkTypeId !== workTypeId) {
          return
        }

        if (entity === 'time') {
          this.lastUsedEntityType.workTypeIdTimes = currentWorkTypeId
          this.lastUsedWorkTypeIdForTimes$.next(currentWorkTypeId)
        } else {
          this.lastUsedEntityType.workTypeIdTasks = currentWorkTypeId
          this.lastUsedWorkTypeIdForTasks$.next(currentWorkTypeId)
        }
      }
    })
  }

  /**
   * Maps a User Setting response to a string array
   * @param {HttpResponse<UserSetting>} response
   * @returns {string[]}
   */
  private mapArrayResponse(response: HttpResponse<UserSetting>): string[] {
    const responseBody = response.body

    if (responseBody) {
      return responseBody.value.split(',')
    } else {
      return []
    }
  }

  /**
   * Adds an id to the start of a list of ids,
   * throwing out old elements if it exceeds the max length
   * @param {string[]} ids
   * @param {string} id
   * @param {number} maxLength
   * @returns {string}
   */
  private addRecentId(ids: string[], id: string, maxLength: number): string {
    const index = ids.indexOf(id, 0)

    if (index > -1) {
      // If the id is already in the list, move it to the begining
      ids.splice(index, 1)
      ids.unshift(id)
    } else {
      // In the case the id is not in the List, put it in the beginning and remove the last element
      ids.unshift(id)

      if (ids.length > maxLength) {
        ids.pop()
      }
    }

    return ids.join()
  }
}
